import {
  W, H, R, DEATH_LINE_Y, LAUNCHER_X, LAUNCHER_Y, NUM_COLORS,
  GameState, Theme, Effect, Button, Phase, Bubble,
} from './types';
import { cellCenter } from './grid';
import { aimPath } from './physics';
import bgUrl from '../img/Bliss.png';

let bg: HTMLImageElement | null = null;

export function setupCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  return ctx;
}

export function loadBackground(): void {
  const img = new Image();
  img.onload = () => { bg = img; };
  img.onerror = () => { bg = null; };
  img.src = bgUrl;
}

export function readTheme(): Theme {
  const cs = getComputedStyle(document.documentElement);
  const v = (n: string) => cs.getPropertyValue(n).trim();
  const balls: string[] = [];
  for (let i = 1; i <= NUM_COLORS; i++) balls.push(v(`--ball-${i}`));
  return {
    bgTop: v('--bg-top'), bgBottom: v('--bg-bottom'),
    wall: v('--wall'), danger: v('--danger'),
    text: v('--text'), textDim: v('--text-dim'), accent: v('--accent'),
    overlayBg: v('--overlay-bg'), btnBg: v('--btn-bg'), btnText: v('--btn-text'),
    balls,
  };
}

// ── Buttons (shared with input) ──
const RESTART_BTN: Button = { x: W - 96, y: 8, w: 88, h: 30 };
const MENU_TOGGLE_BTN: Button = { x: 8, y: 8, w: 36, h: 30 };
export function getButtons(phase: Phase): Record<string, Button> {
  const cx = W / 2;
  if (phase === 'menu') return { play: { x: cx - 80, y: 360, w: 160, h: 52 } };
  if (phase === 'playing') return { restart: RESTART_BTN, menu: MENU_TOGGLE_BTN };
  return { again: { x: cx - 80, y: 380, w: 160, h: 52 } }; // win | lost
}

// ── Pause overlay (☰ → Продолжить / Заново / В хаб) ──
const PAUSE_BW = 200, PAUSE_BH = 44, PAUSE_GAP = 14;
export function getPauseButtons(): Record<string, Button> {
  const cx = W / 2;
  const totalH = PAUSE_BH * 3 + PAUSE_GAP * 2;
  const py = (H - totalH) / 2;
  return {
    resume:  { x: cx - PAUSE_BW / 2, y: py,                            w: PAUSE_BW, h: PAUSE_BH },
    restart: { x: cx - PAUSE_BW / 2, y: py + (PAUSE_BH + PAUSE_GAP),     w: PAUSE_BW, h: PAUSE_BH },
    hub:     { x: cx - PAUSE_BW / 2, y: py + (PAUSE_BH + PAUSE_GAP) * 2, w: PAUSE_BW, h: PAUSE_BH },
  };
}

// ── Helpers ──
function shade(hex: string, pct: number): string {
  const h = hex.replace('#', '');
  const num = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  let r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  r = Math.max(0, Math.min(255, Math.round(r * (1 + pct))));
  g = Math.max(0, Math.min(255, Math.round(g * (1 + pct))));
  b = Math.max(0, Math.min(255, Math.round(b * (1 + pct))));
  return `rgb(${r},${g},${b})`;
}

function drawBall(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, scale = 1): void {
  const r = R * scale;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  // top-left radial highlight
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.1, x, y, r);
  g.addColorStop(0, 'rgba(255,255,255,0.55)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.0)');
  ctx.fillStyle = g;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = shade(color, -0.15);
  ctx.stroke();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawButton(ctx: CanvasRenderingContext2D, b: Button, label: string, theme: Theme, font = 'bold 22px system-ui'): void {
  ctx.fillStyle = theme.btnBg;
  roundRect(ctx, b.x, b.y, b.w, b.h, 10);
  ctx.fill();
  ctx.fillStyle = theme.btnText;
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, b.x + b.w / 2, b.y + b.h / 2 + 1);
  ctx.textBaseline = 'alphabetic';
}

function drawBackground(ctx: CanvasRenderingContext2D, theme: Theme): void {
  if (bg && bg.complete && bg.naturalWidth) {
    const scale = H / bg.naturalHeight;       // cover by height
    const dw = bg.naturalWidth * scale;
    ctx.drawImage(bg, W / 2 - dw / 2, 0, dw, H);
    ctx.fillStyle = 'rgba(15,22,38,0.35)';    // darken for ball contrast
    ctx.fillRect(0, 0, W, H);
  } else {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, theme.bgTop);
    g.addColorStop(1, theme.bgBottom);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }
}

function drawField(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme, effects: Effect[], now: number, shiftY: number): void {
  // Walls + ceiling (fixed, never shifted)
  ctx.strokeStyle = theme.wall;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(2, 0); ctx.lineTo(2, H);
  ctx.moveTo(W - 2, 0); ctx.lineTo(W - 2, H);
  ctx.moveTo(0, 2); ctx.lineTo(W, 2);
  ctx.stroke();

  // Death line
  ctx.save();
  ctx.strokeStyle = theme.danger;
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 6]);
  ctx.beginPath();
  ctx.moveTo(0, DEATH_LINE_Y); ctx.lineTo(W, DEATH_LINE_Y);
  ctx.stroke();
  ctx.restore();

  // Bubbles + effects (slide down during a ceiling drop)
  ctx.save();
  ctx.translate(0, shiftY);

  for (const b of s.bubbles.values()) {
    const c = cellCenter(b.row, b.col, s.dropCount);
    drawBall(ctx, c.x, c.y, theme.balls[b.color]);
  }

  // Effects
  for (const e of effects) {
    const t = Math.max(0, Math.min((now - e.startMs) / e.dur, 1));
    if (e.kind === 'pop') {
      ctx.globalAlpha = 1 - t;
      drawBall(ctx, e.x, e.y, theme.balls[e.color], 1 - t * 0.6);
      ctx.globalAlpha = 1;
    } else if (e.kind === 'fall') {
      ctx.globalAlpha = 1 - t * 0.8;
      drawBall(ctx, e.x, e.y + e.vy * t + 300 * t * t, theme.balls[e.color]);
      ctx.globalAlpha = 1;
    } else if (e.kind === 'snap') {
      drawBall(ctx, e.x, e.y, theme.balls[e.color], 0.85 + 0.15 * t);
    } else if (e.kind === 'particle') {
      ctx.globalAlpha = 1 - t;
      ctx.fillStyle = theme.balls[e.color];
      ctx.beginPath();
      ctx.arc(e.x + e.vx * t, e.y + e.vy * t + 200 * t * t, e.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }
  ctx.restore();
}

function drawAim(ctx: CanvasRenderingContext2D, s: GameState): void {
  const path = aimPath(LAUNCHER_X, LAUNCHER_Y - R, s.aimAngle, s.bubbles, s.dropCount);
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 8]);
  ctx.beginPath();
  ctx.moveTo(path[0].x, path[0].y);
  for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].y);
  ctx.stroke();
  ctx.restore();
  // small marker at the landing point
  const end = path[path.length - 1];
  ctx.strokeStyle = 'rgba(255,255,255,0.6)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(end.x, end.y, R, 0, Math.PI * 2);
  ctx.stroke();
}

function drawLauncher(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme): void {
  // base
  ctx.fillStyle = theme.wall;
  ctx.beginPath();
  ctx.arc(LAUNCHER_X, LAUNCHER_Y, R + 8, 0, Math.PI * 2);
  ctx.fill();
  // current ball
  drawBall(ctx, LAUNCHER_X, LAUNCHER_Y, theme.balls[s.current]);
  // next preview (smaller, to the right)
  ctx.fillStyle = theme.textDim;
  ctx.font = '11px system-ui';
  ctx.textAlign = 'center';
  ctx.fillText('NEXT', LAUNCHER_X + 70, LAUNCHER_Y - 18);
  drawBall(ctx, LAUNCHER_X + 70, LAUNCHER_Y + 2, theme.balls[s.next], 0.62);
}

function drawHUD(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme): void {
  ctx.fillStyle = theme.text;
  ctx.font = 'bold 24px system-ui';
  ctx.textAlign = 'left';
  ctx.fillText(String(s.score), 12, 30);
  ctx.fillStyle = theme.textDim;
  ctx.font = '12px system-ui';
  ctx.fillText('Best ' + s.best, 12, 48);

  // drop counter (centered top)
  ctx.fillStyle = theme.textDim;
  ctx.font = '11px system-ui';
  ctx.textAlign = 'center';
  ctx.fillText('NEXT DROP', W / 2, 18);
  ctx.fillStyle = s.dropCounter <= 2 ? theme.danger : theme.text;
  ctx.font = 'bold 18px system-ui';
  ctx.fillText(String(s.dropCounter), W / 2, 38);

  drawButton(ctx, RESTART_BTN, 'Restart', theme, 'bold 14px system-ui');
  drawButton(ctx, MENU_TOGGLE_BTN, '☰', theme, 'bold 16px system-ui');
}

function dimOverlay(ctx: CanvasRenderingContext2D, theme: Theme): void {
  ctx.fillStyle = theme.overlayBg;
  ctx.fillRect(0, 0, W, H);
}

function drawCenterText(ctx: CanvasRenderingContext2D, text: string, y: number, font: string, color: string): void {
  ctx.fillStyle = color;
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.fillText(text, W / 2, y);
}

function drawMenu(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme): void {
  dimOverlay(ctx, theme);
  drawCenterText(ctx, 'Bubble', 220, 'bold 56px system-ui', theme.text);
  drawCenterText(ctx, 'Shooter', 280, 'bold 56px system-ui', theme.accent);
  // decorative ball row
  for (let i = 0; i < NUM_COLORS; i++) {
    drawBall(ctx, W / 2 - 110 + i * 44, 320, theme.balls[i], 0.8);
  }
  drawButton(ctx, getButtons('menu').play, 'Play', theme);
  if (s.best > 0) drawCenterText(ctx, 'Best: ' + s.best, 450, '16px system-ui', theme.textDim);
}

function drawEnd(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme, win: boolean): void {
  dimOverlay(ctx, theme);
  drawCenterText(ctx, win ? 'Cleared!' : 'Game Over', 250, 'bold 44px system-ui', win ? theme.accent : theme.danger);
  drawCenterText(ctx, 'SCORE', 300, '15px system-ui', theme.textDim);
  drawCenterText(ctx, String(s.score), 348, 'bold 52px system-ui', theme.text);
  drawCenterText(ctx, 'Best: ' + s.best, 374, '16px system-ui', theme.textDim);
  drawButton(ctx, getButtons(win ? 'win' : 'lost').again, win ? 'Play Again' : 'Try Again', theme);
}

function drawPause(ctx: CanvasRenderingContext2D, theme: Theme): void {
  dimOverlay(ctx, theme);
  const btns = getPauseButtons();
  const panelX = btns.resume.x - 24;
  const panelY = btns.resume.y - 40;
  const panelW = btns.resume.w + 48;
  const panelH = (btns.hub.y + btns.hub.h) - btns.resume.y + 64;
  ctx.fillStyle = 'rgba(0,0,0,0.28)';
  roundRect(ctx, panelX, panelY, panelW, panelH, 18);
  ctx.fill();

  drawCenterText(ctx, 'Пауза', panelY + 32, 'bold 22px system-ui', theme.text);
  drawButton(ctx, btns.resume, 'Продолжить', theme, 'bold 17px system-ui');
  drawButton(ctx, btns.restart, 'Заново', theme, 'bold 17px system-ui');

  // «В хаб» — акцентная кнопка
  roundRect(ctx, btns.hub.x, btns.hub.y, btns.hub.w, btns.hub.h, 10);
  ctx.fillStyle = theme.accent;
  ctx.fill();
  ctx.fillStyle = theme.btnText;
  ctx.font = 'bold 17px system-ui';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('В хаб', btns.hub.x + btns.hub.w / 2, btns.hub.y + btns.hub.h / 2 + 1);
  ctx.textBaseline = 'alphabetic';
}

export function renderFrame(
  ctx: CanvasRenderingContext2D, s: GameState, theme: Theme, effects: Effect[], now: number, gridShiftY = 0,
  menuOpen = false,
): void {
  drawBackground(ctx, theme);

  if (s.phase === 'menu') {
    drawField(ctx, s, theme, effects, now, gridShiftY);
    drawMenu(ctx, s, theme);
    return;
  }

  drawField(ctx, s, theme, effects, now, gridShiftY);

  if (s.phase === 'playing') {
    if (!s.projectile) drawAim(ctx, s);
    drawLauncher(ctx, s, theme);
  }

  if (s.projectile) {
    drawBall(ctx, s.projectile.x, s.projectile.y, theme.balls[s.projectile.color]);
  }

  drawHUD(ctx, s, theme);

  if (s.phase === 'win') drawEnd(ctx, s, theme, true);
  else if (s.phase === 'lost') drawEnd(ctx, s, theme, false);
  else if (s.phase === 'playing' && menuOpen) drawPause(ctx, theme);
}

export function bubbleCenter(b: Bubble, dropCount: number): { x: number; y: number } {
  return cellCenter(b.row, b.col, dropCount);
}
